import React from 'react';

export default function Privacy() {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16 bg-white">
      {/* Professional Legal Header */}
      <header className="text-center border-b-2 border-slate-300 pb-12 mb-12">
        <h1 className="text-5xl font-bold mb-6 text-slate-900 tracking-tight">PRIVACY POLICY</h1>
        <div className="text-base text-slate-600 space-y-2 font-medium">
          <p className="text-xl font-semibold text-slate-800">ToolboxLogic, Inc.</p>
          <p className="text-lg">Community Management Platform</p>
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p>
              <span className="font-bold">Effective Date:</span> January 1, 2025
            </p>
            <p>
              <span className="font-bold">Last Updated:</span> September 22, 2025
            </p>
          </div>
        </div>
      </header>

      {/* Legal Document Body */}
      <article className="space-y-12 text-slate-800 leading-relaxed">
        {/* Privacy Commitment Notice */}
        <div className="bg-green-50 border-2 border-green-200 p-10 pb-12 rounded-lg border-l-8 border-l-green-700">
          <h2 className="text-xl font-bold mb-4 text-green-900 uppercase tracking-wider">
            PRIVACY COMMITMENT
          </h2>
          <p className="text-base text-slate-700 font-medium leading-loose">
            TOOLBOXLOGIC, INC. IS COMMITTED TO PROTECTING YOUR PRIVACY AND ENSURING THE SECURITY OF
            YOUR PERSONAL INFORMATION. THIS PRIVACY POLICY EXPLAINS HOW WE COLLECT, USE, DISCLOSE,
            AND SAFEGUARD INFORMATION WHEN YOU USE OUR COMMUNITY MANAGEMENT PLATFORM AND RELATED
            SERVICES.
          </p>
        </div>

        {/* Section 1 */}
        <div className="space-y-8  pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE I<br />
            <span className="text-2xl">SCOPE AND APPLICATION</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 1.01 Applicability</h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> This Privacy Policy applies to all users of
                  the Platform, including community residents, board members, administrators,
                  property managers, vendors, and other authorized users.
                </p>
                <p>
                  <span className="font-bold">(b)</span> This Policy governs our collection, use,
                  and disclosure of information through the Platform and related services operated
                  by ToolboxLogic, Inc.
                </p>
                <p>
                  <span className="font-bold">(c)</span> This Policy applies to information
                  collected both online and offline in connection with Platform services.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 1.02 Relationship to Service Agreements
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> This Privacy Policy supplements but does
                  not replace any privacy provisions in Service Agreements between Company and
                  Community Organizations.
                </p>
                <p>
                  <span className="font-bold">(b)</span> For enterprise clients, additional privacy
                  and data protection terms may apply as specified in their Service Agreements.
                </p>
                <p>
                  <span className="font-bold">(c)</span> In case of conflict between this Policy and
                  a Service Agreement, the Service Agreement shall prevail.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 1.03 Policy Updates</h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> We may update this Privacy Policy from time
                  to time to reflect changes in our practices, technology, legal requirements, or
                  other factors.
                </p>
                <p>
                  <span className="font-bold">(b)</span> We will notify users of material changes
                  through the Platform, email, or other appropriate means.
                </p>
                <p>
                  <span className="font-bold">(c)</span> The updated Policy will be posted with a
                  new effective date, and continued use constitutes acceptance of changes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 2 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE II
            <br />
            <span className="text-2xl">INFORMATION COLLECTION</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.01 Personal Information Categories
              </h3>
              <p className="ml-4 mb-4">
                We collect the following categories of personal information:
              </p>

              <div className="ml-8 space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    2.01(a) Account and Profile Information
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Full name, email address, telephone number, and mailing address</p>
                    <p>
                      • Property ownership, residency information, and community role designation
                    </p>
                    <p>• User credentials, authentication data, and security preferences</p>
                    <p>
                      • Profile preferences, communication settings, and platform customizations
                    </p>
                    <p>
                      • Emergency contact information and accessibility requirements (where
                      provided)
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    2.01(b) Financial Information
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Payment method information (credit card numbers, bank account details)</p>
                    <p>• Assessment payment history, account balances, and transaction records</p>
                    <p>
                      • Investment preferences and portfolio information (Gold/Custom service
                      packages)
                    </p>
                    <p>
                      • Banking relationship data and financial institution information (where
                      authorized)
                    </p>
                    <p>• Tax identification numbers and financial reporting data (as required)</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    2.01(c) Communication and Activity Data
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Messages, forum posts, and community communications</p>
                    <p>• Voting records, governance participation, and meeting attendance</p>
                    <p>• Service requests, work order communications, and vendor interactions</p>
                    <p>• Customer support interactions and feedback submissions</p>
                    <p>• Document uploads and file sharing activities</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.02 Technical Information
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  We automatically collect certain technical information when you use the Platform:
                </p>
                <div className="ml-4 space-y-1 mt-2">
                  <p>
                    <span className="font-bold">(a)</span> Internet Protocol (IP) addresses, device
                    identifiers, and browser information
                  </p>
                  <p>
                    <span className="font-bold">(b)</span> Platform usage data, including features
                    accessed, time spent, and navigation patterns
                  </p>
                  <p>
                    <span className="font-bold">(c)</span> Log files, cookies, web beacons, and
                    similar tracking technologies
                  </p>
                  <p>
                    <span className="font-bold">(d)</span> Location data (where permitted) for
                    facility booking and emergency services
                  </p>
                  <p>
                    <span className="font-bold">(e)</span> Performance metrics, error logs, and
                    system optimization data
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 2.03 Third-Party Information Sources
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> We may receive information from payment
                  processors, banking partners, and financial institutions to verify transactions
                  and prevent fraud.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Property management companies and Community
                  Organizations may provide resident and ownership information for account
                  verification.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Publicly available sources may be used to
                  verify identity and maintain accurate records.
                </p>
                <p>
                  <span className="font-bold">(d)</span> Third-party service providers may share
                  information necessary for integrated Platform functionality.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 3 */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE III
            <br />
            <span className="text-2xl">INFORMATION USE AND PROCESSING</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 3.01 Primary Processing Purposes
              </h3>
              <div className="space-y-4 ml-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    3.01(a) Platform Operation and Service Delivery
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Providing access to Platform features and functionality based on user roles
                    </p>
                    <p>
                      • Processing community governance activities, including voting, elections, and
                      board meetings
                    </p>
                    <p>
                      • Managing facility reservations, community services, and maintenance requests
                    </p>
                    <p>
                      • Processing payments, maintaining financial records, and generating reports
                    </p>
                    <p>
                      • Facilitating communication between community members, vendors, and service
                      providers
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    3.01(b) Security and Account Management
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Creating, maintaining, and securing user accounts and authentication systems
                    </p>
                    <p>
                      • Monitoring for security threats, unauthorized access, and fraudulent
                      activity
                    </p>
                    <p>
                      • Investigating and responding to security incidents and policy violations
                    </p>
                    <p>• Implementing access controls and data protection measures</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    3.01(c) Communication and Support Services
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Sending service notifications, community announcements, and system updates
                    </p>
                    <p>• Providing customer support, technical assistance, and user training</p>
                    <p>
                      • Delivering emergency alerts, safety communications, and critical
                      notifications
                    </p>
                    <p>
                      • Transmitting billing statements, payment reminders, and financial
                      communications
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 3.02 Analytics and Platform Improvement
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> We use aggregated and anonymized data to
                  analyze Platform usage patterns and identify improvement opportunities.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Performance optimization includes
                  monitoring system reliability, speed, and user experience metrics.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Feature development utilizes usage
                  analytics to prioritize enhancements and new functionality.
                </p>
                <p>
                  <span className="font-bold">(d)</span> Security analytics help identify threats
                  and improve protective measures.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 3.03 Legal and Compliance Processing
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span> Compliance with legal obligations under
                  federal, state, and local laws and regulations.
                </p>
                <p>
                  <span className="font-bold">(b)</span> Response to lawful requests from government
                  authorities, law enforcement, and regulatory agencies.
                </p>
                <p>
                  <span className="font-bold">(c)</span> Enforcement of Terms of Service, Service
                  Agreements, and Platform policies.
                </p>
                <p>
                  <span className="font-bold">(d)</span> Protection of Company rights, property,
                  safety, and the rights of users and third parties.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 4 - User Privacy Rights */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE IV
            <br />
            <span className="text-2xl">USER PRIVACY RIGHTS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 4.01 General Data Protection Regulation (GDPR) Rights
              </h3>
              <p className="ml-4 mb-4">
                For users located in the European Economic Area, United Kingdom, or Switzerland, you
                have the following rights under GDPR:
              </p>

              <div className="ml-8 space-y-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">4.01(a) Right of Access</h4>
                  <div className="ml-4 space-y-1">
                    <p>• Request confirmation of whether we process your personal data</p>
                    <p>• Obtain a copy of your personal data in our possession</p>
                    <p>
                      • Receive information about processing purposes, categories, and recipients
                    </p>
                    <p>• Learn about data retention periods and your other rights</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.01(b) Right to Rectification
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Correct inaccurate personal data concerning you</p>
                    <p>• Complete incomplete personal data, including by supplementary statement</p>
                    <p>• Update outdated information in your user profile</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.01(c) Right to Erasure ("Right to be Forgotten")
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Request deletion of personal data when no longer necessary for original
                      purposes
                    </p>
                    <p>• Withdraw consent where processing is based on consent</p>
                    <p>• Object to processing and no overriding legitimate grounds exist</p>
                    <p>• Request deletion when data has been unlawfully processed</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.01(d) Right to Data Portability
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Receive your personal data in a structured, commonly used, machine-readable
                      format
                    </p>
                    <p>• Transmit data to another controller without hindrance from Company</p>
                    <p>
                      • Request direct transmission to another controller where technically feasible
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.01(e) Right to Restriction of Processing
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Limit processing when accuracy of data is contested</p>
                    <p>
                      • Restrict processing when processing is unlawful but deletion is not desired
                    </p>
                    <p>• Limit use when data is no longer needed but required for legal claims</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 4.02 California Consumer Privacy Act (CCPA/CPRA) Rights
              </h3>
              <p className="ml-4 mb-4">
                For California residents, you have the following rights under the California
                Consumer Privacy Act and California Privacy Rights Act:
              </p>

              <div className="ml-8 space-y-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">4.02(a) Right to Know</h4>
                  <div className="ml-4 space-y-1">
                    <p>• Categories and specific pieces of personal information collected</p>
                    <p>• Categories of sources from which information is collected</p>
                    <p>• Business or commercial purposes for collecting or selling information</p>
                    <p>• Categories of third parties with whom information is shared or sold</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">4.02(b) Right to Delete</h4>
                  <div className="ml-4 space-y-1">
                    <p>• Request deletion of personal information collected from you</p>
                    <p>
                      • Subject to certain exceptions for legal compliance and business operations
                    </p>
                    <p>• Verification of identity required prior to deletion</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.02(c) Right to Correct
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Request correction of inaccurate personal information</p>
                    <p>• Update information maintained about you</p>
                    <p>• Provide supporting documentation for correction requests</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    4.02(d) Right to Opt-Out
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Opt-out of sale or sharing of personal information for cross-context
                      behavioral advertising
                    </p>
                    <p>• Limit use and disclosure of sensitive personal information</p>
                    <p>• Withdraw consent for processing of sensitive information</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 4.03 Exercising Your Rights
              </h3>
              <div className="space-y-3 ml-4">
                {/* <p><span className="font-bold">(a)</span> <span className="font-bold">Submission Methods:</span> Submit privacy rights requests via email to privacy@toolboxlogic.com, through your Platform account settings, or by contacting your Community Organization administrator.</p> */}
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Identity Verification:</span> We will verify your
                  identity using information available in your account or by requesting additional
                  documentation to prevent unauthorized access.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Response Timeframes:</span> We will respond to
                  requests within 30 calendar days (GDPR) or 45 calendar days (CCPA), with possible
                  extensions communicated in writing.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">No Discrimination:</span> We will not discriminate
                  against you for exercising your privacy rights, including through denial of
                  services, different pricing, or reduced service quality.
                </p>
                <p>
                  <span className="font-bold">(e)</span>{' '}
                  <span className="font-bold">Authorized Agents:</span> California residents may
                  designate authorized agents to submit requests on their behalf with proper
                  documentation and verification.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 4.04 Additional State Privacy Rights
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Virginia, Colorado, Utah, Connecticut:</span>{' '}
                  Residents of these states have similar rights to access, correct, delete, and
                  opt-out of certain data processing activities.
                </p>
                {/* <p><span className="font-bold">(b)</span> <span className="font-bold">Appeals Process:</span> If we decline your privacy rights request, you may appeal our decision by contacting privacy@toolboxlogic.com within 30 days.</p> */}
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Regulatory Contact:</span> You may contact applicable
                  state privacy authorities if appeals are unsuccessful.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 5 - Data Retention and Deletion */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE V<br />
            <span className="text-2xl">DATA RETENTION AND DELETION</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 5.01 Retention Periods
              </h3>
              <div className="space-y-4 ml-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    5.01(a) Account and Profile Information
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Active account data: Retained for duration of Platform access plus 90 days
                    </p>
                    <p>• Closed account data: Retained for 7 years for legal compliance</p>
                    <p>• Authentication logs: Retained for 2 years for security purposes</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    5.01(b) Financial and Payment Information
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Payment transaction records: Retained for 7 years for tax and audit purposes
                    </p>
                    <p>
                      • Credit card information: Not stored; processed through PCI-compliant
                      processors
                    </p>
                    <p>
                      • Assessment and fee records: Retained per Community Organization requirements
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    5.01(c) Communication and Activity Data
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Community forum posts: Retained for duration of Community Organization
                      agreement
                    </p>
                    <p>
                      • Governance records: Retained per state law requirements (typically 7 years)
                    </p>
                    <p>• Support communications: Retained for 3 years</p>
                    <p>• Technical logs: Retained for 18 months</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 5.02 Legal Basis for Retention
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Legal Compliance:</span> Financial records, governance
                  documents, and audit trails retained to comply with federal, state, and local
                  legal requirements.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Legitimate Interests:</span> Security logs, fraud
                  prevention data, and platform optimization analytics retained for legitimate
                  business interests.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Contract Performance:</span> User account and service
                  data retained to fulfill Service Agreement obligations with Community
                  Organizations.
                </p>
                <p>
                  <span className="font-bold">(d)</span> <span className="font-bold">Consent:</span>{' '}
                  Marketing communications and optional data collection retained based on user
                  consent, withdrawable at any time.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 5.03 Deletion Procedures
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Automated Deletion:</span> Data exceeding retention
                  periods is automatically scheduled for secure deletion within 30 days.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Secure Deletion Methods:</span> Data is permanently
                  deleted using industry-standard secure deletion protocols and cannot be recovered.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Backup Deletion:</span> Data in backup systems is
                  deleted according to backup rotation schedules, typically within 90 days.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">Third-Party Deletion:</span> We instruct third-party
                  processors to delete data consistent with our retention policies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 6 - International Data Transfers */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE VI
            <br />
            <span className="text-2xl">INTERNATIONAL DATA TRANSFERS</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.01 Transfer Locations
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Primary Processing:</span> Personal data is primarily
                  processed in data centers located in the United States.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Third-Party Processors:</span> Some data may be
                  processed by service providers located in Canada, the European Union, and other
                  jurisdictions with adequate data protection laws.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Support Services:</span> Customer support and
                  technical assistance may involve limited data access from international offices.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.02 Transfer Safeguards
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Adequacy Decisions:</span> We transfer data to
                  countries recognized by the European Commission as providing adequate data
                  protection.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Standard Contractual Clauses:</span> For transfers to
                  countries without adequacy decisions, we use EU Standard Contractual Clauses
                  approved by the European Commission.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Additional Safeguards:</span> We implement
                  supplementary measures including encryption, access controls, and audit procedures
                  for international transfers.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">Data Processing Agreements:</span> All international
                  processors are contractually bound to provide equivalent data protection
                  standards.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 6.03 Your Rights Regarding Transfers
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Transfer Information:</span> You may request
                  information about specific countries where your data is processed.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Safeguard Copies:</span> Copies of transfer safeguards
                  (such as Standard Contractual Clauses) are available upon request.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Transfer Objections:</span> You may object to specific
                  transfers where alternative arrangements can be made without affecting service
                  provision.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Article Break - Professional Spacing */}
        <div className="h-12 border-b border-slate-200"></div>

        {/* Section 7 - Cookies, Analytics, and Tracking */}
        <div className="space-y-8 pt-8">
          <h2 className="text-3xl font-bold text-slate-900 border-b-4 border-green-700 pb-4 uppercase tracking-wide">
            ARTICLE VII
            <br />
            <span className="text-2xl">COOKIES, ANALYTICS, AND TRACKING</span>
          </h2>

          <div className="ml-8 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Section 7.01 Cookie Usage</h3>
              <div className="space-y-4 ml-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    7.01(a) Essential Cookies
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>
                      • Authentication and session management cookies (required for Platform
                      functionality)
                    </p>
                    <p>• Security cookies for fraud prevention and account protection</p>
                    <p>• Load balancing cookies for system performance optimization</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    7.01(b) Functional Cookies
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• User preference cookies for interface customization</p>
                    <p>• Language and accessibility setting cookies</p>
                    <p>• Feature usage tracking for service improvement</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-slate-700 mb-3">
                    7.01(c) Analytics Cookies
                  </h4>
                  <div className="ml-4 space-y-1">
                    <p>• Platform usage analytics (anonymized and aggregated)</p>
                    <p>• Performance monitoring and error reporting</p>
                    <p>• User journey analysis for service optimization</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.02 Third-Party Analytics
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Analytics Providers:</span> We may use third-party
                  analytics services to understand Platform usage patterns and improve user
                  experience.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Data Minimization:</span> Analytics data is anonymized
                  and aggregated to prevent individual identification.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Opt-Out Options:</span> Users may opt-out of analytics
                  tracking through browser settings or Platform preferences.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Section 7.03 Cookie Management
              </h3>
              <div className="space-y-3 ml-4">
                <p>
                  <span className="font-bold">(a)</span>{' '}
                  <span className="font-bold">Consent Management:</span> We provide cookie consent
                  banners for users in jurisdictions requiring explicit consent.
                </p>
                <p>
                  <span className="font-bold">(b)</span>{' '}
                  <span className="font-bold">Browser Controls:</span> Users may control cookies
                  through browser settings, though this may affect Platform functionality.
                </p>
                <p>
                  <span className="font-bold">(c)</span>{' '}
                  <span className="font-bold">Cookie Expiration:</span> Cookies are set with
                  appropriate expiration periods and are regularly reviewed for necessity.
                </p>
                <p>
                  <span className="font-bold">(d)</span>{' '}
                  <span className="font-bold">Do Not Track:</span> We respect browser "Do Not Track"
                  signals where technically feasible.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* End of document content */}

        {/* Footer */}
        <div className="mt-16 pt-8 border-t-2 border-slate-300 text-center">
          <p className="text-sm text-slate-600 font-medium">
            © 2025 ToolboxLogic, Inc. All rights reserved.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            This Privacy Policy constitutes a legally binding agreement.{' '}
            {/* For privacy inquiries, contact privacy@toolboxlogic.com */}
          </p>
        </div>
      </article>
    </section>
  );
}
